import {
  WATER_USE_BENCHMARK_CATALOG,
  validateWaterUseBenchmarkCatalog,
} from '../../data/economicContribution/waterUseBenchmarks';
import {
  ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
  WATER_USE_SCREENING_MODEL_VERSION,
  type WaterUseBenchmarkCatalogSnapshot,
  type WaterUseBenchmarkSourceRef,
  type WaterUseAmbientConditions,
  type WaterUseDryCoolingCapacityStatus,
  type WaterUseDryCoolingDesignScreen,
  type WaterUseInputContext,
  type WaterUseRangeAssumption,
  type WaterUseScenarioResult,
  type WaterUseScreeningInput,
  type WaterUseScreeningResult,
} from '../../types/economicContribution';
import type { TmyDataset, TmyHour } from '../../types/tmy';
import {
  calculateAdiabaticWaterScreening,
  type AdiabaticWaterScreeningResult,
} from './adiabaticWaterScreening';
import { hashEconomicModelInput, roundEconomic } from './modelUtils';
import { WET_BULB_BASIS_LABELS } from '../water/waterScreeningLabels';
import { tmyCoincidentWetBulbC } from './tmyCoincidentWetBulb';

export const LITERS_PER_US_GALLON = 3.785411784 as const;
export const KWH_PER_BTU = 0.000_293_071_070_172_222_2 as const;
export const GALLONS_PER_DAY_PER_IT_MW_AT_WUE_ONE = 1_000 * 24 / LITERS_PER_US_GALLON;

export interface WaterUseScreeningOptions {
  /** Site-coordinate-selected, nearest-grid 8,760-hour TMY. */
  tmy?: TmyDataset | null;
  /** Set by the authenticated server when the site-coordinate TMY lookup fails. */
  tmyUnavailable?: boolean;
}

export interface WaterUseScreeningCurrentnessOptions {
  /**
   * Require the server-owned hourly-climate lookup to have completed or
   * returned an explicit unavailable result. This rejects legacy/local results
   * that silently skipped TMY loading while preserving a disclosed provider
   * outage as a usable non-hourly screen.
   */
  requireTmyAttempt?: boolean;
}

/** Catalog/model currency guard for persisted screening snapshots. */
export function isCurrentWaterUseScreeningResult(
  result: WaterUseScreeningResult | null | undefined,
  options: WaterUseScreeningCurrentnessOptions = {},
): boolean {
  return Boolean(
    result
    && result.modelVersion === WATER_USE_SCREENING_MODEL_VERSION
    && result.sourceCatalogVersion === WATER_USE_BENCHMARK_CATALOG.catalogVersion
    && (!options.requireTmyAttempt
      || result.adiabaticClimateStatus?.status === 'calculated'
      || result.adiabaticClimateStatus?.status === 'unavailable')
  );
}

function finite(value: number): boolean {
  return Number.isFinite(value);
}

function validLoadRange(input: WaterUseScreeningInput): boolean {
  return finite(input.loadRange.low)
    && finite(input.loadRange.high)
    && input.loadRange.low > 0
    && input.loadRange.high >= input.loadRange.low;
}

function validPue(range: WaterUseRangeAssumption): boolean {
  return finite(range.low)
    && finite(range.high)
    && range.low >= 1
    && range.high >= range.low;
}

function validUtilization(range: WaterUseRangeAssumption): boolean {
  return finite(range.low)
    && finite(range.high)
    && range.low > 0
    && range.high >= range.low
    && range.high <= 1;
}

function validCycles(range: WaterUseRangeAssumption): boolean {
  return finite(range.low)
    && finite(range.high)
    && range.low > 1
    && range.high >= range.low;
}

function validAmbient(ambient: WaterUseAmbientConditions): boolean {
  return finite(ambient.averageDryBulbC)
    && finite(ambient.peakDryBulbC)
    && finite(ambient.averageWetBulbC)
    && finite(ambient.designWetBulbC)
    && finite(ambient.relativeHumidityPct)
    && Number.isInteger(ambient.monthlyClimatePoints)
    && ambient.averageDryBulbC >= -90
    && ambient.averageDryBulbC <= 70
    && ambient.peakDryBulbC >= ambient.averageDryBulbC
    && ambient.peakDryBulbC <= 70
    && ambient.averageWetBulbC >= -90
    && ambient.averageWetBulbC <= ambient.averageDryBulbC
    && ambient.designWetBulbC >= ambient.averageWetBulbC
    && ambient.designWetBulbC <= ambient.peakDryBulbC
    && ambient.relativeHumidityPct >= 0
    && ambient.relativeHumidityPct <= 100
    && (ambient.wetBulbBasis === 'noaa-design' || ambient.wetBulbBasis === 'sed-peak-proxy')
    && ambient.monthlyClimatePoints >= 0
    && ambient.monthlyClimatePoints <= 12
    && ambient.source === 'site-record'
    && ambient.sourceLabel.trim().length > 0
    && ambient.sedId.trim().length > 0
    && Number.isInteger(ambient.sedVersion)
    && ambient.sedVersion >= 0;
}

function unique<T>(values: readonly T[]): T[] {
  return [...new Set(values)];
}

function refsFor(
  catalog: WaterUseBenchmarkCatalogSnapshot,
  sourceRefIds: readonly string[],
): WaterUseBenchmarkSourceRef[] {
  const ids = new Set(sourceRefIds);
  return catalog.sources.filter((source) => ids.has(source.id));
}

function roundedBand(low: number, high: number, digits = 2): { low: number; high: number } {
  return {
    low: roundEconomic(Math.min(low, high), digits),
    high: roundEconomic(Math.max(low, high), digits),
  };
}

function unavailable(
  input: WaterUseScreeningInput,
  catalog: WaterUseBenchmarkCatalogSnapshot,
  reason: NonNullable<WaterUseScreeningResult['unavailableReason']>,
  limitations: readonly string[],
): WaterUseScreeningResult {
  return {
    schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
    modelVersion: WATER_USE_SCREENING_MODEL_VERSION,
    status: 'not-calculated',
    siteId: input.siteId,
    estimateBasis: 'modelled-screening',
    loadRange: input.loadRange,
    resolvedContext: null,
    scenarios: [],
    unavailableReason: reason,
    modelInputHash: hashEconomicModelInput({ input, catalogVersion: catalog.catalogVersion }),
    sourceCatalogVersion: catalog.catalogVersion,
    sourceRefIds: [],
    limitations,
  };
}

function resolveContext(
  input: WaterUseScreeningInput,
  catalog: WaterUseBenchmarkCatalogSnapshot,
): {
  pue: WaterUseRangeAssumption;
  utilization: WaterUseRangeAssumption;
  heatRejection: NonNullable<WaterUseInputContext['heatRejection']>;
} {
  return {
    pue: input.context?.pue ?? {
      low: catalog.defaults.pue.low,
      high: catalog.defaults.pue.high,
      source: catalog.defaults.pue.source,
      sourceLabel: catalog.defaults.pue.sourceLabel,
    },
    utilization: input.context?.utilization ?? {
      low: catalog.defaults.utilization.low,
      high: catalog.defaults.utilization.high,
      source: catalog.defaults.utilization.source,
      sourceLabel: catalog.defaults.utilization.sourceLabel,
    },
    heatRejection: input.context?.heatRejection ?? 'unknown',
  };
}

function assumptionSourceIds(
  input: WaterUseScreeningInput,
  catalog: WaterUseBenchmarkCatalogSnapshot,
): string[] {
  return [
    ...(input.context?.pue ? [] : catalog.defaults.pue.sourceRefIds),
    ...(input.context?.utilization ? [] : catalog.defaults.utilization.sourceRefIds),
    ...(input.context?.ambientConditions ? [
      'lbnl-site-climate-cooling-model-2024',
      'doe-femp-climate-economizer-controls',
    ] : []),
    ...(input.context?.workload || input.context?.operatingModel || input.context?.rackCoolingStrategy
      ? ['lbnl-site-climate-cooling-model-2024']
      : []),
  ];
}

function contextAssumptions(input: WaterUseScreeningInput): string[] {
  const context = input.context;
  if (!context) return [];
  const ambient = context.ambientConditions;
  return [
    ...(context.operatingModel && context.operatingModel !== 'unknown'
      ? [`Recorded operating model: ${context.operatingModel}.`]
      : []),
    ...(context.workload && context.workload !== 'unknown'
      ? [`Recorded workload: ${context.workload}; it informs heat-density and cooling-system suitability, not an arbitrary heat multiplier.`]
      : []),
    ...(context.rackCoolingStrategy && context.rackCoolingStrategy !== 'undecided'
      ? [`Recorded IT cooling loop: ${context.rackCoolingStrategy}.`]
      : []),
    ...(ambient ? [
      `Local climate from ${ambient.sourceLabel}: ${ambient.averageDryBulbC}°C average and ${ambient.peakDryBulbC}°C peak dry bulb; ${ambient.averageWetBulbC}°C average and ${ambient.designWetBulbC}°C ${WET_BULB_BASIS_LABELS[ambient.wetBulbBasis]} screening wet bulb; ${ambient.relativeHumidityPct}% average relative humidity.`,
      'Local climate qualifies cooling-system suitability, but no hourly economizer or hybrid wet-mode schedule is fabricated from annual/monthly summaries.',
    ] : []),
  ];
}

function climateLimitations(input: WaterUseScreeningInput): string[] {
  return input.context?.ambientConditions
    ? ['The local SED climate is disclosed for design context; the quantity is not numerically climate-adjusted without hourly weather, set points, equipment curves, and control schedules.']
    : ['Trusted exact-current site climate was unavailable, so this quantity uses the cited industry benchmark without a local ambient adjustment.'];
}

function itMwRange(
  input: WaterUseScreeningInput,
  pue: WaterUseRangeAssumption,
): { low: number; high: number } {
  if (input.loadRange.basis === 'it') {
    return { low: input.loadRange.low, high: input.loadRange.high };
  }
  return {
    low: input.loadRange.low / pue.high,
    high: input.loadRange.high / pue.low,
  };
}

function facilityHeatMwRange(
  input: WaterUseScreeningInput,
  pue: WaterUseRangeAssumption,
): { low: number; high: number } {
  if (input.loadRange.basis === 'it') {
    return {
      low: input.loadRange.low * pue.low,
      high: input.loadRange.high * pue.high,
    };
  }
  return { low: input.loadRange.low, high: input.loadRange.high };
}

function buildWueScenarios(
  input: WaterUseScreeningInput,
  catalog: WaterUseBenchmarkCatalogSnapshot,
  pue: WaterUseRangeAssumption,
  utilization: WaterUseRangeAssumption,
): WaterUseScenarioResult[] {
  const itMw = itMwRange(input, pue);
  const assumptionRefs = assumptionSourceIds(input, catalog);
  return catalog.wueBenchmarks.map((benchmark) => {
    const lowGpd = itMw.low
      * GALLONS_PER_DAY_PER_IT_MW_AT_WUE_ONE
      * utilization.low
      * benchmark.lowLitersPerKwhIt;
    const highGpd = itMw.high
      * GALLONS_PER_DAY_PER_IT_MW_AT_WUE_ONE
      * utilization.high
      * benchmark.highLitersPerKwhIt;
    const daily = roundedBand(lowGpd, highGpd);
    const sourceRefIds = unique([...benchmark.sourceRefIds, ...assumptionRefs]);
    const assumptions = [
      `Recorded load range: ${input.loadRange.low}-${input.loadRange.high} MW (${input.loadRange.basis}).`,
      `PUE range: ${pue.low}-${pue.high} (${pue.source}).`,
      `Annual utilization range: ${utilization.low}-${utilization.high} (${utilization.source}).`,
      ...contextAssumptions(input),
    ];
    return {
      scenarioId: benchmark.id,
      kind: benchmark.scenario,
      label: benchmark.label,
      estimateBasis: 'modelled-screening',
      applicability: 'benchmark',
      flowBasis: benchmark.flowBasis,
      boundary: 'onsite-direct-water',
      annualGallons: { ...roundedBand(daily.low * 365, daily.high * 365), unit: 'gallons/year' },
      averageGallonsPerDay: { ...daily, unit: 'gal/day' },
      coefficient: {
        low: benchmark.lowLitersPerKwhIt,
        high: benchmark.highLitersPerKwhIt,
        unit: 'liters-per-kwh-it',
      },
      sourceRefs: refsFor(catalog, sourceRefIds),
      assumptions,
      limitations: [
        ...benchmark.limitations,
        'This is a modeled screening range, not metered, reported, permitted, requested, or design water use for the subject site.',
        ...(input.loadRange.basis === 'utility-service'
          ? ['Utility-service MW is treated as the facility electrical ceiling for this sensitivity; reserve and service-design margins may overstate operating load.']
          : []),
        ...climateLimitations(input),
      ],
      trace: [{
        id: `${benchmark.id}-average-gpd`,
        expression: 'IT MW x 1,000 kW/MW x 24 h/day x utilization x WUE L/kWh / 3.785411784 L/gal',
        inputs: {
          itMwLow: roundEconomic(itMw.low, 4),
          itMwHigh: roundEconomic(itMw.high, 4),
          utilizationLow: utilization.low,
          utilizationHigh: utilization.high,
          wueLow: benchmark.lowLitersPerKwhIt,
          wueHigh: benchmark.highLitersPerKwhIt,
        },
        output: `${daily.low}-${daily.high} gal/day`,
        sourceEntryIds: sourceRefIds,
      }],
    };
  });
}

function wetTowerApplicability(
  heatRejection: NonNullable<WaterUseInputContext['heatRejection']>,
): WaterUseScenarioResult['applicability'] | null {
  if (heatRejection === 'wet-cooling-tower') return 'selected-technology';
  if (
    heatRejection === 'dry-cooler'
    || heatRejection === 'free-cooling'
    || heatRejection === 'hybrid'
    || heatRejection === 'once-through'
    || heatRejection === 'unknown'
    || heatRejection === 'absorption-cchp'
  ) {
    return 'sensitivity';
  }
  return null;
}

function dryApplicability(
  heatRejection: NonNullable<WaterUseInputContext['heatRejection']>,
): WaterUseScenarioResult['applicability'] | null {
  if (heatRejection === 'dry-cooler' || heatRejection === 'free-cooling') return 'selected-technology';
  if (
    heatRejection === 'wet-cooling-tower'
    || heatRejection === 'hybrid'
    || heatRejection === 'once-through'
    || heatRejection === 'unknown'
    || heatRejection === 'absorption-cchp'
  ) {
    return 'sensitivity';
  }
  return null;
}

function buildWetTowerScenarios(
  input: WaterUseScreeningInput,
  catalog: WaterUseBenchmarkCatalogSnapshot,
  pue: WaterUseRangeAssumption,
  utilization: WaterUseRangeAssumption,
  applicability: WaterUseScenarioResult['applicability'],
  cycles: WaterUseRangeAssumption,
): WaterUseScenarioResult[] {
  const benchmark = catalog.wetTower;
  const heatMw = facilityHeatMwRange(input, pue);
  const kwhHeatRejectionPerNominalCoolingTowerTonHour =
    benchmark.nominalCoolingTowerTonBtuPerHour * KWH_PER_BTU;
  const tonHoursPerDayPerMw = 1_000 * 24
    / kwhHeatRejectionPerNominalCoolingTowerTonHour;
  const evaporationCoefficient = benchmark.evaporationGallonsPerTonHour;
  const makeupCoefficient = input.context?.cyclesOfConcentration
    ? {
        low: evaporationCoefficient * cycles.high / (cycles.high - 1),
        high: evaporationCoefficient * cycles.low / (cycles.low - 1),
      }
    : benchmark.makeupGallonsPerTonHour;
  const sourceRefIds = unique([
    ...benchmark.sourceRefIds,
    ...assumptionSourceIds(input, catalog),
  ]);
  const sourceRefs = refsFor(catalog, sourceRefIds);

  const scenario = (
    kind: Extract<WaterUseScenarioResult['kind'], 'wet-cooling-tower-consumption' | 'wet-cooling-tower-withdrawal'>,
    label: string,
    flowBasis: Extract<WaterUseScenarioResult['flowBasis'], 'consumption' | 'withdrawal'>,
    coefficient: { low: number; high: number },
  ): WaterUseScenarioResult => {
    const lowGpd = heatMw.low * utilization.low * tonHoursPerDayPerMw * coefficient.low;
    const highGpd = heatMw.high * utilization.high * tonHoursPerDayPerMw * coefficient.high;
    const fullLoadLow = heatMw.low * tonHoursPerDayPerMw * coefficient.low;
    const fullLoadHigh = heatMw.high * tonHoursPerDayPerMw * coefficient.high;
    const daily = roundedBand(lowGpd, highGpd);
    const fullLoad = roundedBand(fullLoadLow, fullLoadHigh);
    return {
      scenarioId: `${benchmark.id}-${flowBasis}`,
      kind,
      label,
      estimateBasis: 'modelled-screening',
      applicability,
      flowBasis,
      boundary: 'cooling-process-only',
      annualGallons: { ...roundedBand(daily.low * 365, daily.high * 365), unit: 'gallons/year' },
      averageGallonsPerDay: { ...daily, unit: 'gal/day' },
      fullLoadGallonsPerDay: { ...fullLoad, unit: 'gal/day' },
      coefficient: { ...coefficient, unit: 'gallons-per-ton-hour' },
      sourceRefs,
      assumptions: [
        'All modeled facility electrical input is treated as heat rejected at the cooling-tower boundary.',
        `Nominal cooling-tower ton basis: ${benchmark.nominalCoolingTowerTonBtuPerHour.toLocaleString('en-US')} Btu/h of total heat rejection (ASHRAE).`,
        `Annual utilization range: ${utilization.low}-${utilization.high} (${utilization.source}).`,
        `Cooling-tower cycles of concentration: ${cycles.low}-${cycles.high} (${cycles.source}).`,
        ...contextAssumptions(input),
      ],
      limitations: [
        ...benchmark.limitations,
        'Average annual and 100%-load sensitivities are not peak-day, permitted, requested, design, or metered subject-site quantities.',
        ...(applicability === 'sensitivity'
          ? ['The subject heat-rejection design is not a confirmed wet-only tower; this row is a technology sensitivity, not the selected design.']
          : []),
        ...climateLimitations(input),
      ],
      trace: [{
        id: `${benchmark.id}-${flowBasis}-gpd`,
        expression: 'facility heat MW x 1,000 kW/MW x 24 h/day / (nominal cooling-tower-ton Btu/h x 0.0002930710701722222 kWh/Btu) x utilization x gal/nominal cooling-tower-ton-hour',
        inputs: {
          facilityHeatMwLow: roundEconomic(heatMw.low, 4),
          facilityHeatMwHigh: roundEconomic(heatMw.high, 4),
          utilizationLow: utilization.low,
          utilizationHigh: utilization.high,
          nominalCoolingTowerTonBtuPerHour:
            benchmark.nominalCoolingTowerTonBtuPerHour,
          kwhHeatRejectionPerNominalCoolingTowerTonHour: roundEconomic(
            kwhHeatRejectionPerNominalCoolingTowerTonHour,
            9,
          ),
          coefficientLow: roundEconomic(coefficient.low, 6),
          coefficientHigh: roundEconomic(coefficient.high, 6),
        },
        output: `${daily.low}-${daily.high} gal/day`,
        sourceEntryIds: sourceRefIds,
      }],
    };
  };

  return [
    scenario(
      'wet-cooling-tower-consumption',
      'Wet cooling-tower evaporation consumption sensitivity',
      'consumption',
      { low: evaporationCoefficient, high: evaporationCoefficient },
    ),
    scenario(
      'wet-cooling-tower-withdrawal',
      'Wet cooling-tower makeup withdrawal sensitivity',
      'withdrawal',
      makeupCoefficient,
    ),
  ];
}

function classifyDryCoolingApproach(
  maxFacilitySupplyC: number,
  coilInletC: { low: number; high: number },
  approachC: { low: number; high: number },
): WaterUseDryCoolingCapacityStatus {
  const worstCaseHeadroomC = maxFacilitySupplyC - coilInletC.high;
  const bestCaseHeadroomC = maxFacilitySupplyC - coilInletC.low;
  if (worstCaseHeadroomC >= approachC.high) return 'dry-only-screening-candidate';
  if (bestCaseHeadroomC >= approachC.low) return 'vendor-selection-required';
  if (bestCaseHeadroomC > 0) return 'assist-or-trim-required';
  return 'reference-temperature-exceeded';
}

function buildDryCoolingDesignScreen(
  input: WaterUseScreeningInput,
  catalog: WaterUseBenchmarkCatalogSnapshot,
  pue: WaterUseRangeAssumption,
  validatedTmyHours?: readonly TmyHour[],
  pairedTmyWetBulbC?: number | null,
): WaterUseDryCoolingDesignScreen | undefined {
  const ambient = input.context?.ambientConditions;
  const rackCooling = input.context?.rackCoolingStrategy;
  if (!ambient || (rackCooling !== 'liquid' && rackCooling !== 'hybrid')) return undefined;

  const benchmark = catalog.dryCoolingScreening;
  const dryBulbC = ambient.peakDryBulbC;
  // Owner decision (28 Sep 2026): prefer the wet bulb coincident with the
  // hottest validated TMY hours. The SED screening wet bulb is a monthly mean
  // or assumed-RH estimate, not coincident with peak heat. Neither is an
  // ASHRAE design percentile, so the adiabatic statuses remain sensitivities.
  // A read-only SED projection may pass its validated frozen paired mean.
  // No ambient/summary is added to the persisted economic request identity.
  const tmyWetBulbC = pairedTmyWetBulbC ?? (validatedTmyHours ? tmyCoincidentWetBulbC(validatedTmyHours) : null);
  const wetBulbC = tmyWetBulbC ?? ambient.designWetBulbC;
  const wetBulbDepressionC = Math.max(0, dryBulbC - wetBulbC);
  const adiabaticCoilInletC = roundedBand(
    dryBulbC - benchmark.adiabaticSaturationEffectiveness.high * wetBulbDepressionC,
    dryBulbC - benchmark.adiabaticSaturationEffectiveness.low * wetBulbDepressionC,
  );
  const dryCoilInletC = { low: dryBulbC, high: dryBulbC };
  const seriesHeatExchangerApproachC = roundedBand(
    benchmark.cduApproachC + benchmark.additionalPlateHeatExchangerApproachC.low,
    benchmark.cduApproachC + benchmark.additionalPlateHeatExchangerApproachC.high,
  );
  const totalAmbientToFacilitySupplyApproachC = roundedBand(
    benchmark.dryCoolerApproachC.low + seriesHeatExchangerApproachC.low,
    benchmark.dryCoolerApproachC.high + seriesHeatExchangerApproachC.high,
  );
  const screeningDutyMw = roundedBand(
    facilityHeatMwRange(input, pue).low,
    facilityHeatMwRange(input, pue).high,
    4,
  );

  return {
    basis: 'published-engineering-range-screening',
    sitePeakDryBulbProxyC: dryBulbC,
    siteWetBulbC: wetBulbC,
    wetBulbBasis: tmyWetBulbC !== null ? 'tmy-coincident' : ambient.wetBulbBasis,
    screeningDutyMw,
    dryCoolerApproachC: { ...benchmark.dryCoolerApproachC },
    cduApproachC: benchmark.cduApproachC,
    additionalPlateHeatExchangerApproachC: {
      ...benchmark.additionalPlateHeatExchangerApproachC,
    },
    seriesHeatExchangerApproachC,
    totalAmbientToFacilitySupplyApproachC,
    adiabaticSaturationEffectiveness: { ...benchmark.adiabaticSaturationEffectiveness },
    adiabaticCoilInletC,
    classChecks: benchmark.waterClasses.map((waterClass) => ({
      waterClass: waterClass.id,
      maxFacilitySupplyC: waterClass.maxFacilitySupplyC,
      dryStatus: classifyDryCoolingApproach(
        waterClass.maxFacilitySupplyC,
        dryCoilInletC,
        totalAmbientToFacilitySupplyApproachC,
      ),
      dryRequiredFacilitySupplyC: roundedBand(
        dryBulbC + totalAmbientToFacilitySupplyApproachC.low,
        dryBulbC + totalAmbientToFacilitySupplyApproachC.high,
      ),
      adiabaticStatus: classifyDryCoolingApproach(
        waterClass.maxFacilitySupplyC,
        adiabaticCoilInletC,
        totalAmbientToFacilitySupplyApproachC,
      ),
      adiabaticRequiredFacilitySupplyC: roundedBand(
        adiabaticCoilInletC.low + totalAmbientToFacilitySupplyApproachC.low,
        adiabaticCoilInletC.high + totalAmbientToFacilitySupplyApproachC.high,
      ),
    })),
    sourceRefIds: [...benchmark.sourceRefIds],
    limitations: [...benchmark.limitations],
  };
}

function buildDryScenario(
  input: WaterUseScreeningInput,
  catalog: WaterUseBenchmarkCatalogSnapshot,
  pue: WaterUseRangeAssumption,
  utilization: WaterUseRangeAssumption,
  applicability: WaterUseScenarioResult['applicability'],
  tmy: TmyDataset | null | undefined,
): WaterUseScenarioResult {
  const adiabaticWaterScreening: AdiabaticWaterScreeningResult | undefined = tmy
    ? calculateAdiabaticWaterScreening({
        tmy,
        facilityHeatMw: facilityHeatMwRange(input, pue),
        utilization: { low: utilization.low, high: utilization.high },
      })
    : undefined;
  // Only a TMY year the adiabatic screen accepted may supply the coincident wet bulb.
  const dryCoolingDesignScreen = buildDryCoolingDesignScreen(
    input,
    catalog,
    pue,
    tmy && adiabaticWaterScreening?.status === 'calculated' ? tmy.hours : undefined,
  );
  const sourceRefIds = unique([
    'lbnl-dry-cooling-water-sensitivity-2024',
    'doe-femp-hybrid-data-center-cooling',
    'ashrae-ai-dry-cooling-integration',
    ...(input.context?.ambientConditions ? [
      'lbnl-site-climate-cooling-model-2024',
      'doe-femp-climate-economizer-controls',
    ] : []),
    ...(dryCoolingDesignScreen?.sourceRefIds ?? []),
  ]);
  return {
    scenarioId: 'dry-cooling-process-water-v1',
    kind: 'dry-cooling',
    label: 'Dry heat-rejection cooling-water sensitivity',
    estimateBasis: 'modelled-screening',
    applicability,
    flowBasis: 'cooling-water',
    boundary: 'cooling-process-only',
    designQualification: 'dry-capacity-unverified',
    ...(dryCoolingDesignScreen ? { dryCoolingDesignScreen } : {}),
    ...(adiabaticWaterScreening ? { adiabaticWaterScreening } : {}),
    annualGallons: { low: 0, high: 0, unit: 'gallons/year' },
    averageGallonsPerDay: { low: 0, high: 0, unit: 'gal/day' },
    fullLoadGallonsPerDay: { low: 0, high: 0, unit: 'gal/day' },
    coefficient: { low: 0, high: 0, unit: 'gallons-per-ton-hour' },
    sourceRefs: refsFor(catalog, sourceRefIds),
    assumptions: [
      'The modeled cold-end heat rejection uses no evaporative cooling water.',
      ...contextAssumptions(input),
    ],
    limitations: [
      'Zero applies only to modeled evaporative cooling-process water, not total site water.',
      'Zero is a conditional dry-operation endpoint, not proof that the selected equipment can reject the full site heat load at the local design dry-bulb temperature.',
      'Domestic use, humidification, fire-system testing, construction water, cleaning, and other ancillary uses remain unmodeled.',
      ...(applicability === 'sensitivity'
        ? ['The subject heat-rejection design is not confirmed dry-only; this row is a technology sensitivity.']
        : []),
      ...(input.context?.ambientConditions
        ? [dryCoolingDesignScreen
            ? 'A published engineering-range temperature screen is included; no manufacturer MW-capacity curve or numeric equipment derate is fabricated.'
            : 'Recorded peak dry-bulb temperature informs equipment sizing and derating review; a warm-water class screen requires a recorded liquid or hybrid IT cooling loop.']
        : []),
      ...(dryCoolingDesignScreen?.limitations ?? []),
    ],
    trace: [
      {
        id: 'dry-cooling-process-water',
        expression: 'evaporative cooling-process coefficient = 0 under dry heat rejection',
        inputs: { recordedMwLow: input.loadRange.low, recordedMwHigh: input.loadRange.high },
        output: '0 gal/day cooling-process water',
        sourceEntryIds: sourceRefIds,
      },
      ...(dryCoolingDesignScreen ? [{
        id: 'dry-cooling-temperature-headroom',
        expression: 'required FWS = coil-inlet air + dry-cooler approach + CDU approach + optional series plate-HX approach; adiabatic coil inlet = DB - effectiveness x (DB - WB)',
        inputs: {
          sitePeakDryBulbProxyC: dryCoolingDesignScreen.sitePeakDryBulbProxyC,
          siteWetBulbC: dryCoolingDesignScreen.siteWetBulbC,
          wetBulbBasis: dryCoolingDesignScreen.wetBulbBasis,
          dryApproachLowC: dryCoolingDesignScreen.dryCoolerApproachC.low,
          dryApproachHighC: dryCoolingDesignScreen.dryCoolerApproachC.high,
          cduApproachC: dryCoolingDesignScreen.cduApproachC,
          additionalPlateHeatExchangerApproachLowC:
            dryCoolingDesignScreen.additionalPlateHeatExchangerApproachC.low,
          additionalPlateHeatExchangerApproachHighC:
            dryCoolingDesignScreen.additionalPlateHeatExchangerApproachC.high,
          totalAmbientToFacilitySupplyApproachLowC:
            dryCoolingDesignScreen.totalAmbientToFacilitySupplyApproachC.low,
          totalAmbientToFacilitySupplyApproachHighC:
            dryCoolingDesignScreen.totalAmbientToFacilitySupplyApproachC.high,
          adiabaticEffectivenessLow: dryCoolingDesignScreen.adiabaticSaturationEffectiveness.low,
          adiabaticEffectivenessHigh: dryCoolingDesignScreen.adiabaticSaturationEffectiveness.high,
        },
        output: dryCoolingDesignScreen.classChecks
          .map((check) => `${check.waterClass}: dry=${check.dryStatus}; adiabatic=${check.adiabaticStatus}`)
          .join('; '),
        sourceEntryIds: dryCoolingDesignScreen.sourceRefIds,
      }] : []),
    ],
  };
}

/**
 * Deterministic early-stage water-use screen. AI research may populate input
 * context and catalog artifacts, but it never performs or overrides this math.
 * Every returned scenario is modeled screening evidence, never subject-site
 * actual, requested, permitted, or design water use.
 */
export function calculateWaterUseScreening(
  input: WaterUseScreeningInput,
  catalog: WaterUseBenchmarkCatalogSnapshot = WATER_USE_BENCHMARK_CATALOG,
  options: WaterUseScreeningOptions = {},
): WaterUseScreeningResult {
  const catalogErrors = validateWaterUseBenchmarkCatalog(catalog);
  if (catalogErrors.length > 0) {
    return unavailable(input, catalog, 'benchmark-catalog-invalid', catalogErrors);
  }
  if (!input.siteId.trim() || !validLoadRange(input)) {
    return unavailable(input, catalog, 'invalid-input', [
      'A site id and finite, positive, ordered MW endpoints are required.',
      'An exact MW value is not required; equal endpoints represent an exact value.',
    ]);
  }

  const resolved = resolveContext(input, catalog);
  if (!validPue(resolved.pue) || !validUtilization(resolved.utilization)
    || (input.context?.cyclesOfConcentration != null
      && !validCycles(input.context.cyclesOfConcentration))
    || (input.context?.ambientConditions != null
      && !validAmbient(input.context.ambientConditions))) {
    return unavailable(input, catalog, 'invalid-assumption', [
      'PUE must be finite, ordered, and at least 1.',
      'Utilization must be finite, ordered, greater than 0, and no greater than 1.',
      'Cooling-tower cycles of concentration, when supplied, must be finite, ordered, and greater than 1.',
      'Ambient conditions, when supplied, must be physically ordered finite values from an exact current site record.',
    ]);
  }

  const wetApplicability = wetTowerApplicability(resolved.heatRejection);
  const dryScenarioApplicability = dryApplicability(resolved.heatRejection);
  const cycles: WaterUseRangeAssumption | undefined = wetApplicability == null
    ? undefined
    : input.context?.cyclesOfConcentration ?? {
        low: catalog.wetTower.defaultCyclesOfConcentration.low,
        high: catalog.wetTower.defaultCyclesOfConcentration.high,
        source: 'screening-assumption',
        sourceLabel: 'EPA WaterSense 3-5 cycles-of-concentration sensitivity',
      };

  const scenarios: WaterUseScenarioResult[] = buildWueScenarios(
    input,
    catalog,
    resolved.pue,
    resolved.utilization,
  );
  if (wetApplicability && cycles) {
    scenarios.push(...buildWetTowerScenarios(
      input,
      catalog,
      resolved.pue,
      resolved.utilization,
      wetApplicability,
      cycles,
    ));
  }
  if (dryScenarioApplicability) {
    scenarios.push(buildDryScenario(
      input,
      catalog,
      resolved.pue,
      resolved.utilization,
      dryScenarioApplicability,
      options.tmy,
    ));
  }
  const hasDryCoolingDesignScreen = scenarios.some(
    (scenario) => scenario.dryCoolingDesignScreen != null,
  );
  const adiabaticWaterScreening = scenarios
    .find((scenario) => scenario.kind === 'dry-cooling')
    ?.adiabaticWaterScreening;
  const hasHourlyAdiabaticScreen = adiabaticWaterScreening?.status === 'calculated';

  const sourceRefIds = unique(scenarios.flatMap((scenario) => scenario.sourceRefs.map((source) => source.id)));
  const itLoad = roundedBand(
    itMwRange(input, resolved.pue).low,
    itMwRange(input, resolved.pue).high,
    4,
  );
  const facilityHeat = roundedBand(
    facilityHeatMwRange(input, resolved.pue).low,
    facilityHeatMwRange(input, resolved.pue).high,
    4,
  );
  const modelInputHash = hashEconomicModelInput({
    input,
    resolvedContext: {
      ...resolved,
      cyclesOfConcentration: cycles,
      itLoadMw: itLoad,
      facilityHeatMw: facilityHeat,
    },
    catalogVersion: catalog.catalogVersion,
    ...(options.tmy ? {
      tmy: {
        schemaVersion: options.tmy.schemaVersion,
        latitudeRounded: options.tmy.latitudeRounded,
        longitudeRounded: options.tmy.longitudeRounded,
        source: options.tmy.source,
        datasetYear: options.tmy.datasetYear,
        fetchedAt: options.tmy.fetchedAt,
        contentHash: hashEconomicModelInput(options.tmy.hours),
      },
    } : {}),
  });
  return {
    schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
    modelVersion: WATER_USE_SCREENING_MODEL_VERSION,
    status: 'calculated',
    siteId: input.siteId,
    estimateBasis: 'modelled-screening',
    loadRange: input.loadRange,
    resolvedContext: {
      pue: resolved.pue,
      utilization: resolved.utilization,
      heatRejection: resolved.heatRejection,
      ...(cycles ? { cyclesOfConcentration: cycles } : {}),
      itLoadMw: itLoad,
      facilityHeatMw: facilityHeat,
      ...(input.context?.workload && input.context.workload !== 'unknown'
        ? { workload: input.context.workload }
        : {}),
      ...(input.context?.operatingModel && input.context.operatingModel !== 'unknown'
        ? { operatingModel: input.context.operatingModel }
        : {}),
      ...(input.context?.rackCoolingStrategy && input.context.rackCoolingStrategy !== 'undecided'
        ? { rackCoolingStrategy: input.context.rackCoolingStrategy }
        : {}),
      ...(input.context?.ambientConditions ? { ambientConditions: input.context.ambientConditions } : {}),
      climateTreatment: hasHourlyAdiabaticScreen
        ? 'tmy-hourly-adiabatic-screening'
        : input.context?.ambientConditions
        ? hasDryCoolingDesignScreen
          ? 'site-context-design-screen-no-hourly-adjustment'
          : 'site-context-no-hourly-adjustment'
        : 'not-available',
    },
    scenarios,
    adiabaticClimateStatus: hasHourlyAdiabaticScreen
      ? { status: 'calculated' }
      : adiabaticWaterScreening?.status === 'not-calculated'
        ? { status: 'unavailable', reason: 'invalid-tmy' }
        : options.tmyUnavailable
          ? { status: 'unavailable', reason: 'tmy-unavailable' }
          : { status: 'not-requested' },
    modelInputHash,
    sourceCatalogVersion: catalog.catalogVersion,
    sourceRefIds,
    limitations: [
      'All values are deterministic early-stage screening ranges; none is metered, reported, requested, permitted, or a design commitment for the subject site.',
      'Annual-average ranges are distinct from the 100%-load cooling sensitivity and must not be described as peak-day demand.',
      'Workload, operating model, and rack-side cooling describe heat density and architecture; total heat remains derived from MW, PUE, and utilization rather than an arbitrary workload multiplier.',
      ...(hasHourlyAdiabaticScreen
        ? ['A site-coordinate-selected, nearest-grid 8,760-hour TMY drives the separate adiabatic-assist water range; generic airflow, pad effectiveness, activation thresholds, and cycles of concentration remain disclosed screening assumptions.']
        : input.context?.ambientConditions
          ? [hasDryCoolingDesignScreen
            ? 'Exact-current local dry-bulb and wet-bulb conditions support a published-range temperature-headroom screen; they do not become a false-precision OEM derate or annual operating schedule.'
            : 'Exact-current local dry-bulb, wet-bulb, and humidity conditions are carried into the result for suitability review; they do not become a false-precision annual operating schedule.']
          : ['No trusted exact-current local climate was available for this run.']),
      ...(adiabaticWaterScreening?.status === 'not-calculated'
        ? [`The site-coordinate-selected TMY failed the adiabatic screen validation: ${adiabaticWaterScreening.validationErrors.join(' ')}`]
        : []),
      ...(resolved.heatRejection === 'unknown'
        ? ['Heat rejection is unknown, so wet and dry rows are separate technology sensitivities rather than one blended prediction.']
        : []),
      ...(resolved.heatRejection === 'hybrid'
        ? ['The hybrid wet-operation fraction is unknown; wet and dry sensitivities are not blended into a false-precision hybrid range.']
        : []),
      ...(resolved.heatRejection === 'once-through'
        ? ['Once-through cooling is not converted to consumption without a sourced return-flow factor; no once-through technology quantity is fabricated.']
        : []),
      ...(resolved.heatRejection === 'absorption-cchp'
        ? ['The legacy absorption/CCHP value describes cooling generation rather than a definitive cold-end sink, so wet and dry sensitivities remain separate.']
        : []),
    ],
  };
}

/** Derive a screening-only view from frozen evidence, without annual recalculation. */
export function dryCoolingDesignScreenFromClimate(
  water: WaterUseScreeningResult,
  ambient: WaterUseAmbientConditions | undefined,
  pairedWetBulbC: number | null = null,
): WaterUseDryCoolingDesignScreen | undefined {
  const context = water.resolvedContext;
  if (water.status !== 'calculated' || !isCurrentWaterUseScreeningResult(water) || !context || !ambient
    || !validLoadRange({ schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION, siteId: water.siteId, loadRange: water.loadRange })
    || !validPue(context.pue)
    || !Number.isFinite(water.loadRange.high * context.pue.high)
    || !validAmbient(ambient)
    || (pairedWetBulbC !== null && (!Number.isFinite(pairedWetBulbC) || pairedWetBulbC < -100 || pairedWetBulbC > ambient.peakDryBulbC))) return undefined;
  return buildDryCoolingDesignScreen({
    schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION, siteId: water.siteId, loadRange: water.loadRange,
    context: { pue: context.pue, rackCoolingStrategy: context.rackCoolingStrategy, ambientConditions: ambient },
  }, WATER_USE_BENCHMARK_CATALOG, context.pue, undefined, pairedWetBulbC);
}

export function dryCoolingDesignSourceRefs(
  screen: WaterUseDryCoolingDesignScreen,
): readonly WaterUseBenchmarkSourceRef[] {
  return WATER_USE_BENCHMARK_CATALOG.sources.filter(source => screen.sourceRefIds.includes(source.id));
}
